import { useState } from "react";
import {
  MapPin, Clock, Phone, CheckCircle, XCircle, AlertTriangle,
  Gift, Calendar, ExternalLink, Shield, RefreshCw,
} from "lucide-react";
import { API, axios } from "@/api";
import { EaseStars } from "@/components/shared/EaseStars";
import { UrgencyBadge } from "@/components/shared/UrgencyBadge";
import { HoursStatusBadge } from "@/components/shared/HoursStatusBadge";
import { DietaryPills } from "@/components/shared/DietaryPills";
import WaitTimeDisplay from "@/components/shared/WaitTimeDisplay";
import { toast } from "sonner";
import { trackLinkClick } from "@/lib/track";
import { useOfferDetail } from "./OfferDetailContext";
import EaseExplainer from "./EaseExplainer";

export default function OfferSummaryCard() {
  const { offer, user, cc, closesUrgent, userHasVerified, recentChange, tracker, setShowWaitSheet, getText, fetchOffer, businessId } = useOfferDetail();

  const [remindedNextYear, setRemindedNextYear] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);
  const [participationReported, setParticipationReported] = useState(false);
  const [reportingParticipation, setReportingParticipation] = useState(false);

  const handleRemindNextYear = async () => {
    setSavingReminder(true);
    try {
      const res = await axios.post(`${API}/deals/remind-next-year`, { business_id: businessId });
      setRemindedNextYear(true);
      toast.success(res.data.message || "Saved for next year!");
    } catch {
      toast.error("Couldn't save reminder");
    } finally {
      setSavingReminder(false);
    }
  };

  const handleParticipationReport = async (participates) => {
    setReportingParticipation(true);
    try {
      const nl = offer?.nearest_location;
      await axios.post(`${API}/reports/location-participation`, {
        business_id: businessId,
        location_id: nl?.location_id || null,
        participates,
      });
      setParticipationReported(true);
      toast.success(participates
        ? "Thanks! You confirmed this location participates. +10 pts"
        : "Thanks for letting us know. +10 pts"
      );
      fetchOffer();
    } catch {
      toast.error("Couldn't submit report");
    } finally {
      setReportingParticipation(false);
    }
  };

  return (
    <div data-detail-card="white" data-testid="offer-summary-card">
      {offer.offer_summary && <p className="text-slate-600 text-sm mb-3">{offer.offer_summary}</p>}
      {offer.alt_offer_notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3" data-testid="alt-offer-notes">
          <p className="text-xs font-semibold text-amber-700 mb-0.5">Also available:</p>
          <p className="text-sm text-amber-800">{offer.alt_offer_notes}</p>
        </div>
      )}
      {offer.signup_deadline && (() => {
        const missed = offer.signup_missed;
        const approaching = offer.signup_approaching;
        const leadDays = offer.signup_lead_days || offer.offer_signup_lead_days || 0;
        const daysRemaining = offer.signup_days_remaining ?? 0;

        if (missed) return (
          <div className="rounded-xl px-3.5 py-3 mb-3 bg-amber-50 border border-amber-200" data-testid="signup-missed-card">
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  This deal needed {leadDays} days advance signup
                  {offer.days_until_birthday != null && offer.days_until_birthday >= 0
                    ? ` — your birthday is in ${offer.days_until_birthday} day${offer.days_until_birthday !== 1 ? "s" : ""}`
                    : " — your birthday window has passed"}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  You may have missed the window. Some businesses are flexible — it's worth trying anyway.
                </p>
              </div>
            </div>
            {!remindedNextYear && (
              <button
                onClick={handleRemindNextYear}
                disabled={savingReminder}
                className="mt-2.5 w-full text-center text-sm font-medium py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 transition-colors"
                data-testid="remind-next-year-btn"
              >
                {savingReminder ? "Saving..." : "Remind me next year"}
              </button>
            )}
            {remindedNextYear && (
              <p className="mt-2 text-xs text-amber-700 font-medium text-center" data-testid="reminded-next-year-msg">
                Saved — we'll remind you before next year's birthday
              </p>
            )}
          </div>
        );

        if (approaching) return (
          <div className="rounded-xl px-3.5 py-3 mb-3 bg-sky-50 border border-sky-200" data-testid="signup-approaching-card">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-sky-800">
                  {daysRemaining} day{daysRemaining !== 1 ? "s" : ""} left to sign up
                </p>
                <p className="text-xs text-sky-600 mt-0.5">
                  Signup takes about 2 minutes. Don't miss this one.
                </p>
              </div>
            </div>
            {offer.signup_url && (
              <a
                href={offer.signup_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackLinkClick("signup_url_approaching", offer.business_id)}
                className="mt-2.5 w-full flex items-center justify-center gap-1.5 text-sm font-medium py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white transition-colors"
                data-testid="signup-now-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Sign up now
              </a>
            )}
          </div>
        );

        return null;
      })()}
      {/* Coverage location awareness */}
      {offer.coverage && offer.coverage === "franchise_varies" && (() => {
        const nl = offer.nearest_location;
        const pStatus = nl?.participation_status;
        const locPhone = nl?.phone || offer.phone;
        const locName = nl?.location_name || nl?.city;

        if (pStatus === "confirmed_participating") return (
          <div className="rounded-xl px-3.5 py-2.5 mb-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200" data-testid="location-participation-confirmed">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-700">{locName ? `${locName} participates` : "Your nearest location participates"}</p>
              <p className="text-xs text-emerald-600 mt-0.5">This {offer.name} location has been confirmed to offer the birthday deal.</p>
            </div>
          </div>
        );

        if (pStatus === "confirmed_not_participating") return (
          <div className="rounded-xl px-3.5 py-2.5 mb-3 flex items-start gap-2 bg-red-50 border border-red-200" data-testid="location-participation-denied">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700">{locName ? `${locName} does not participate` : "Your nearest location does not participate"}</p>
              <p className="text-xs text-red-600 mt-0.5">Try another {offer.name} location — franchise participation varies.</p>
            </div>
          </div>
        );

        if (pStatus === "verify_locally") return (
          <div className="rounded-xl px-3.5 py-3 mb-3 bg-amber-50 border border-amber-200" data-testid="location-participation-verify">
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Call ahead to confirm{locName ? ` — ${locName}` : ""}</p>
                <p className="text-xs text-amber-600 mt-0.5">We couldn't verify this location's participation. Call before visiting to confirm they offer the birthday deal.</p>
                {locPhone && !/varies|n\/a/i.test(locPhone) && (
                  <a href={`tel:${locPhone}`} className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded-lg transition-colors" data-testid="participation-call-btn">
                    <Phone className="w-3 h-3" />{locPhone}
                  </a>
                )}
              </div>
            </div>
          </div>
        );

        return (
          <div className="rounded-xl px-3.5 py-2.5 mb-3 flex items-start gap-2 bg-slate-50 border border-slate-200" data-testid="location-coverage-card">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" />
            <p className="text-sm text-slate-600">Availability may vary by location — confirm with your nearest {offer.name} before visiting.</p>
          </div>
        );
      })()}
      {/* Crowdsource participation buttons */}
      {offer.coverage === "franchise_varies" && user && !participationReported && (
        <div className="rounded-xl px-3.5 py-2.5 mb-3 bg-white border border-slate-200" data-testid="participation-report-card">
          <p className="text-xs font-semibold text-slate-500 mb-2">Visited this location? Help others:</p>
          <div className="flex gap-2">
            <button onClick={() => handleParticipationReport(true)} disabled={reportingParticipation} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors disabled:opacity-50" data-testid="report-participates-btn">
              <CheckCircle className="w-3.5 h-3.5" />Yes, they participate
            </button>
            <button onClick={() => handleParticipationReport(false)} disabled={reportingParticipation} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors disabled:opacity-50" data-testid="report-not-participates-btn">
              <XCircle className="w-3.5 h-3.5" />No, they don't
            </button>
          </div>
        </div>
      )}
      {offer.coverage === "franchise_varies" && participationReported && (
        <div className="rounded-xl px-3 py-2 mb-3 flex items-center gap-2 bg-sky-50 border border-sky-200" data-testid="participation-reported-msg">
          <CheckCircle className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
          <p className="text-xs font-medium text-sky-600">Thanks for your report! It helps the community.</p>
        </div>
      )}
      {/* ALL PILLS */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {offer.is_true_freebie && <span className="px-2 py-1 bg-teal-50 text-teal-600 border border-teal-200 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1" data-testid="true-freebie-detail"><Gift className="w-3 h-3" />True Freebie</span>}
        {offer.offer_requires_app && <span className="px-2 py-1 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg text-[11px] font-semibold">Requires App</span>}
        {offer.offer_requires_app === false && <span className="px-2 py-1 bg-purple-50 text-purple-600 border border-purple-200 rounded-lg text-[11px] font-semibold">No App</span>}
        {(offer.offer_signup_lead_days || 0) > 0 && <span className="px-2 py-1 bg-amber-50 text-amber-600 border border-amber-200 rounded-lg text-[11px] font-semibold">Requires Signup</span>}
        {offer.min_spend > 0 && <span className="px-2 py-1 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg text-[11px] font-semibold">${offer.min_spend} min</span>}
        {!offer.offer_purchase_required && <span className="px-2 py-1 bg-green-50 text-green-600 border border-green-200 rounded-lg text-[11px] font-semibold">No Purchase</span>}
        <UrgencyBadge offer={offer} />
        {offer.hours_status?.status_label && <HoursStatusBadge offer={offer} />}
        <DietaryPills dietary={offer.dietary} boycottFlag={offer.boycott_flag} showBoycott={user?.boycott_unlocked} compact />
      </div>

      {/* WAIT TIME */}
      {offer.wait_time_reported_at && (
        <div className="mb-3">
          <WaitTimeDisplay
            waitMinutes={offer.wait_time_reported}
            waitCategory={offer.wait_time_category}
            reportedAt={offer.wait_time_reported_at}
            reporterCount={offer.wait_time_reported_by_count}
            onTap={() => setShowWaitSheet(true)}
          />
        </div>
      )}

      {/* DASHED DIVIDER */}
      <div className="border-t border-dashed border-slate-200 my-3" />

      {/* RECEIPT-STYLE ROWS */}
      <div className="space-y-0" data-testid="receipt-rows">
        {(() => {
          const nl = offer.nearest_location;
          const locDisplay = nl
            ? `${nl.city}, ${nl.state}`
            : (offer.coverage === "Nationwide" ? "Nationwide" : offer.city ? `${offer.city}, ${offer.state || ""}` : "See website");
          return (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-xs text-slate-400 flex items-center gap-1.5"><MapPin className="w-3 h-3" />Location</span>
              <span className="text-xs font-semibold text-slate-700">{locDisplay}</span>
            </div>
          );
        })()}
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-xs text-slate-400 flex items-center gap-1.5"><Shield className="w-3 h-3" />Verified</span>
          <span className={`text-xs font-semibold ${offer.crowd_verification_count > 0 ? "text-emerald-600" : "text-slate-400"}`}>
            {offer.crowd_verification_count > 0 ? `${offer.crowd_verification_count}x confirmed` : "Not yet"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-xs text-slate-400 flex items-center gap-1.5"><Clock className="w-3 h-3" />Hours</span>
          <span className={`text-xs font-semibold ${closesUrgent ? "text-amber-600" : "text-slate-700"}`}>
            {offer.hours_status?.status_label || "Check website"}
          </span>
        </div>
        {!userHasVerified && (
          <p className="text-[10px] text-slate-400 italic py-1 px-0.5" data-testid="verify-hours-prompt">{getText("verify_hours")}</p>
        )}
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-xs text-slate-400 flex items-center gap-1.5"><CheckCircle className="w-3 h-3" />Claimed</span>
          <span className="text-xs font-semibold text-slate-700">{(offer.times_claimed || offer.claim_count || 0) > 0 ? `${offer.times_claimed || offer.claim_count}x` : "Be first"}</span>
        </div>
        {recentChange && (
          <div className="flex items-start justify-between py-2 border-b border-slate-100" data-testid="recent-change-row">
            <span className="text-xs text-slate-400 flex items-center gap-1.5 flex-shrink-0"><RefreshCw className="w-3 h-3" />Recent change</span>
            <span className="text-xs font-semibold text-slate-700 text-right max-w-[200px]">
              {recentChange.description || recentChange.change_type.replace(/_/g, " ")}
              <span className="text-slate-400 font-normal"> · {new Date(recentChange.changed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            </span>
          </div>
        )}
        <div className="flex items-center justify-between py-2">
          <span className="text-xs text-slate-400">Ease of claim</span>
          <div className="flex items-center gap-1.5">
            <EaseStars stars={offer.ease_stars} />
            {offer.complexity_label && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${offer.complexity_label === "Simple" ? "bg-green-50 text-green-600" : offer.complexity_label === "Moderate" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`} data-testid="complexity-label">
                {offer.complexity_label}
              </span>
            )}
          </div>
        </div>
      </div>
      <EaseExplainer offer={offer} />
    </div>
  );
}
